/**
 * Entry point kết nối và khởi động ứng dụng Fake Excel / Multi-Theme ngụy trang đọc truyện
 * Hỗ trợ các nền tảng:
 * 1. Google Sheets (Bảng tính kế toán)
 * 2. Gmail Web (Hộp thư nội bộ)
 * 3. Zalo Web (Khung chat trao đổi công việc)
 *
 * Hỗ trợ tham số URL chuẩn:
 * 🔗 ?theme=sheets|email|zalo&source=s1&story=ten-truyen&chap=chuong-1
 * Mặc định khi vào trang chủ: không nạp truyện nào, giữ trạng thái an toàn.
 */
import { DEFAULT_SOURCE } from './config/constants.js';
import { SpreadsheetModule } from './modules/spreadsheet.js';
import { StoryReaderModule } from './modules/storyReader.js';
import { BossKeyModule } from './modules/bossKey.js';
import { SearchModalModule } from './modules/searchModal.js';
import { ThemeManagerModule } from './modules/themeManager.js';
import { HotkeyManagerModule } from './modules/hotkeyManager.js';
import { StorageService } from './services/storageService.js';
import { UrlService } from './services/urlService.js';
import { TTSEngine } from './tts/ttsEngine.js';
import { TTSLoadingModal } from './tts/ui/ttsLoadingModal.js';
import { TTSControlBar } from './tts/ui/ttsControlBar.js';
import { TTS_EVENTS } from './tts/constants/ttsEvents.js';
import { VideoPlayerEngine } from './modules/video/videoPlayerEngine.js';
import { createLazyApps, prefetchOnIntent } from './apps/lazyApps.js';
import { AppRouter } from './modules/appRouter.js';
import { WorkspaceHomeController } from './modules/workspace/workspaceHomeController.js';
import { SecurityGuard } from './modules/securityGuard.js';
import { VideoHotkeyHandler } from './modules/video/videoHotkeyHandler.js';
import { GoogleAccountController } from './modules/workspace/googleAccountController.js';
import { AuthGate, restorePendingReturnUrl } from './modules/auth/authGate.js';

/**
 * Khởi động toàn bộ ứng dụng
 */
function initApp() {
  console.log('[App] Đang khởi chạy hệ thống...');

  // 0. CỔNG ĐĂNG NHẬP: mọi chức năng chỉ dùng được sau khi đăng nhập Google.
  // Khôi phục link gốc (kể cả query ?story=...&chap=...) nếu vừa đăng nhập xong, trước khi module nào đọc URL.
  restorePendingReturnUrl();
  const authGate = new AuthGate({ initialCheck: window.__authCheck || null });
  window.__authCheck = null;
  window.authGate = authGate;

  // 1. Khởi tạo Spreadsheet Engine (Lưới bảng tính Google Sheets)
  const spreadsheet = new SpreadsheetModule();
  try {
    spreadsheet.renderColumnHeaders();
    spreadsheet.renderRowHeaders();
    spreadsheet.renderGrid();
    spreadsheet.selectCell(1, 'A');
  } catch (err) {
    console.warn('Lỗi khi render lưới bảng tính ban đầu:', err);
  }

  // 2. Khởi tạo Story Reader Module (Đọc truyện đa nền tảng: Sheets, Gmail, Zalo)
  const storyReader = new StoryReaderModule(spreadsheet);

  // 3. Khởi tạo Boss Key Module (Nhấn phím Esc để ngụy trang tức thì)
  const bossKey = new BossKeyModule(spreadsheet, storyReader);
  window.bossKeyModule = bossKey;

  // 3.1. Khởi tạo Module Bảo vệ An ninh & Chống DevTools / Anti-Debugger
  // Kiểm tra môi trường: Localhost thì mở tự do, Production thì chặn F12 và kích hoạt Debugger
  const securityGuard = new SecurityGuard({
    onPanicTrigger: () => bossKey.toggle()
  });
  securityGuard.init();
  window.securityGuard = securityGuard;

  // Keep the Google account identity consistent across Workspace, Sheets, Gmail and Zalo headers.
  const googleAccountController = new GoogleAccountController();
  window.googleAccountController = googleAccountController;

  // 4. Khởi tạo Search Modal Module (Tìm kiếm truyện)
  const searchModal = new SearchModalModule(storyReader);
  window.searchModal = searchModal;

  // 5. Khởi tạo Theme Manager Module (Chuyển đổi giao diện: Sheets / Gmail / Zalo / CapCut / Canva)
  const themeManager = new ThemeManagerModule(storyReader, searchModal);
  window.themeManager = themeManager;
  // Chỉ kích hoạt theme truyện ban đầu nếu người dùng không truy cập liên kết Video
  if (!UrlService.isVideoUrl()) {
    try {
      themeManager.initInitialTheme();
    } catch (err) {
      console.warn('Lỗi khi áp dụng theme ban đầu:', err);
    }
  }

  // 6. Khởi tạo Hotkey Manager Module (Toàn bộ phím tắt điều hướng, thoát hiểm & bảng hướng dẫn)
  const hotkeyManager = new HotkeyManagerModule(storyReader, themeManager, searchModal, bossKey);
  window.hotkeyManager = hotkeyManager;

  // 6.3. KHỞI TẠO HỆ THỐNG TTS CORE ENGINE (ÂM THANH ĐỌC TRUYỆN ĐA NỀN TẢNG)
  const ttsEngine = new TTSEngine();
  const ttsLoadingModal = new TTSLoadingModal();
  const ttsControlBar = new TTSControlBar(ttsEngine);
  window.ttsEngine = ttsEngine;
  window.ttsLoadingModal = ttsLoadingModal;
  window.ttsControlBar = ttsControlBar;

  // Kết nối TTS với StoryReader để tự động nạp tài liệu khi chuyển chương
  storyReader.ttsEngine = ttsEngine;

  // Gắn sự kiện bật/tắt đọc truyện cho các nút Loa trên header
  const toggleTtsAction = () => ttsControlBar.toggle();
  const btnTtsSheets = document.getElementById('btn-tts-sheets');
  if (btnTtsSheets) btnTtsSheets.addEventListener('click', toggleTtsAction);

  const btnTtsGmail = document.getElementById('btn-tts-gmail');
  if (btnTtsGmail) btnTtsGmail.addEventListener('click', toggleTtsAction);

  const btnTtsZalo = document.getElementById('btn-tts-zalo');
  if (btnTtsZalo) btnTtsZalo.addEventListener('click', toggleTtsAction);

  // Phím tắt Alt + Space để Bật / Tắt đọc truyện nhanh
  window.addEventListener('keydown', (e) => {
    if (e.altKey && e.code === 'Space') {
      e.preventDefault();
      toggleTtsAction();
    }
  });

  // TỰ ĐỘNG ĐỒNG BỘ VÀ HIGHLIGHT CÂU ĐANG ĐỌC TRÊN CẢ 3 GIAO DIỆN
  ttsEngine.on(TTS_EVENTS.SEGMENT_CHANGED, (data) => {
    // 1. Google Sheets: Highlight hàng đang đọc và chọn ô A tương ứng
    document.querySelectorAll('.grid-row.tts-reading-row').forEach(r => r.classList.remove('tts-reading-row'));
    const targetRow = data.paragraphIndex + 1;
    const rowEl = document.querySelector(`.grid-row[data-row="${targetRow}"]`);
    if (rowEl) {
      rowEl.classList.add('tts-reading-row');
      rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      spreadsheet.selectCell(targetRow, 'A');
    }

    // 2. Zalo Web: Highlight tin nhắn chat và cuộn mượt mà
    document.querySelectorAll('.zalo-message-item.tts-reading-chat').forEach(m => m.classList.remove('tts-reading-chat'));
    const msgItems = document.querySelectorAll('.zalo-message-item');
    if (msgItems && msgItems[data.paragraphIndex]) {
      msgItems[data.paragraphIndex].classList.add('tts-reading-chat');
      msgItems[data.paragraphIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // 3. Gmail Web: Highlight đoạn văn bản email báo cáo
    document.querySelectorAll('#gmail-email-body p.tts-reading-email').forEach(p => p.classList.remove('tts-reading-email'));
    const emailParagraphs = document.querySelectorAll('#gmail-email-body p');
    // Bỏ qua 2 dòng chào hỏi đầu email
    const pTarget = data.paragraphIndex + 2;
    if (emailParagraphs && emailParagraphs[pTarget]) {
      emailParagraphs[pTarget].classList.add('tts-reading-email');
      emailParagraphs[pTarget].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // 4. Đồng bộ tiến độ câu đọc vào Session của Workspace
    storyReader.updateSentenceProgress(data.paragraphIndex);
  });

  // Khi click vào ô trong Sheets: Tự động nhảy tới câu đọc tương ứng
  document.getElementById('grid-cells')?.addEventListener('click', (e) => {
    const cell = e.target.closest('.cell-item');
    if (cell && cell.dataset.row) {
      const rowNum = parseInt(cell.dataset.row, 10);
      if (ttsEngine.isPlaying && rowNum >= 1) {
        ttsEngine.seekToParagraph(rowNum - 1);
      }
    }
  });

  // Khi bấm phím Esc (Boss Key): Ngắt âm thanh tức thì để ngụy trang an toàn
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      ttsEngine.stop();
      ttsControlBar.hide();
      if (window.emulatorController) {
        window.emulatorController.deactivate();
      }
      if (window.figmaController) {
        window.figmaController.deactivate();
      }
      if (window.vscodeController) {
        window.vscodeController.deactivate();
      }
      if (window.videoController) {
        window.videoController.panicExit();
      }
    }
  });


  // 6.2. GẮN SỰ KIỆN CHO THANH ĐIỀU KHIỂN CHẠM NHANH TRÊN DI ĐỘNG (MOBILE TOUCH BAR)
  const mobileBtnSearch = document.getElementById('mobile-btn-search');
  if (mobileBtnSearch) {
    mobileBtnSearch.addEventListener('click', () => searchModal.open());
  }

  const mobileBtnPrev = document.getElementById('mobile-btn-prev');
  if (mobileBtnPrev) {
    mobileBtnPrev.addEventListener('click', () => storyReader.navigateChapter('prev'));
  }

  const mobileBtnNext = document.getElementById('mobile-btn-next');
  if (mobileBtnNext) {
    mobileBtnNext.addEventListener('click', () => storyReader.navigateChapter('next'));
  }

  const mobileBtnTheme = document.getElementById('mobile-btn-theme');
  if (mobileBtnTheme) {
    mobileBtnTheme.addEventListener('click', () => themeManager.openModal());
  }

  const mobileBtnPanic = document.getElementById('mobile-btn-panic');
  if (mobileBtnPanic) {
    mobileBtnPanic.addEventListener('click', () => hotkeyManager.panicExit());
  }


  // Gắn sự kiện mở popup tìm kiếm truyện cho thanh tìm kiếm và nút soạn trên Gmail & Zalo
  const gmailSearchBox = document.getElementById('gmail-search-box');
  if (gmailSearchBox) {
    gmailSearchBox.addEventListener('click', () => searchModal.open());
  }
  const gmailComposeBtn = document.getElementById('gmail-compose-btn');
  if (gmailComposeBtn) {
    gmailComposeBtn.addEventListener('click', () => searchModal.open());
  }
  const zaloSearchBox = document.getElementById('zalo-search-box');
  if (zaloSearchBox) {
    zaloSearchBox.addEventListener('click', () => searchModal.open());
  }

  // 7. NÚT "CHIA SẺ" TRÊN HEADER GOOGLE SHEETS: Tự động copy link chứa source & story & chap & theme
  const btnShare = document.getElementById('btn-share-link');
  if (btnShare) {
    btnShare.addEventListener('click', async () => {
      const success = await UrlService.copyShareLink();
      if (success) {
        const originalHtml = btnShare.innerHTML;
        btnShare.classList.add('copied');
        btnShare.innerHTML = `
          <svg viewBox="0 0 24 24" width="16" height="16" fill="#0b8043"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
          <span style="color:#0b8043;font-weight:700;">Đã sao chép link!</span>
        `;
        setTimeout(() => {
          btnShare.classList.remove('copied');
          btnShare.innerHTML = originalHtml;
        }, 2500);
      } else {
        alert('Đường dẫn hiện tại: ' + window.location.href);
      }
    });
  }

  // 7.1. KHỞI TẠO CORE ENGINE PHÁT VIDEO VÀ 2 BỘ ĐIỀU KHIỂN GIAO DIỆN ĐỘC LẬP
  // Core call API & engine dùng chung, giao diện CapCut Web & Canva Video tách riêng 100%
  const videoPlayerEngine = new VideoPlayerEngine();
  window.videoPlayerEngine = videoPlayerEngine;

  // CapCut, Canva, Caro, Emulator, Figma, VS Code được tải lười (lazy):
  // JS + CSS + HTML của mỗi app chỉ tải khi người dùng mở app đó lần đầu.
  const lazyApps = createLazyApps({
    playerEngine: videoPlayerEngine,
    onPanicExit: () => bossKey.toggle()
  });
  const {
    capcutController,
    canvaController,
    calendarController,
    emulatorController,
    figmaController,
    vscodeController,
    adminController
  } = lazyApps;
  Object.assign(window, lazyApps);
  prefetchOnIntent(lazyApps);

  // 7.2. KHỞI TẠO CONTROLLER WORKSPACE HOMEPAGE TRUNG TÂM
  const workspaceHomeController = new WorkspaceHomeController();
  window.workspaceHomeController = workspaceHomeController;

  // 7.5. KHỞI TẠO BỘ ĐIỀU HƯỚNG TỔNG THỂ HASH APP ROUTER
  const appRouter = new AppRouter({
    authGate,
    themeManager,
    storyReader,
    playerEngine: videoPlayerEngine,
    capcutController,
    canvaController,
    homeController: workspaceHomeController,
    searchModal,
    calendarController,
    emulatorController,
    figmaController,
    vscodeController,
    adminController
  });
  workspaceHomeController.setRouter(appRouter);
  searchModal.setVideoSelectHandler((videoId) => {
    appRouter.navigateTo(`#/capcut?v=${encodeURIComponent(videoId)}&t=0`);
  });
  window.appRouter = appRouter;

  // 7.6. KHỞI TẠO BỘ PHÍM TẮT ĐIỀU KHIỂN VIDEO (Space, <, >, M, F)
  const videoHotkeys = new VideoHotkeyHandler(videoPlayerEngine, {
    capcutController,
    canvaController
  });
  window.videoHotkeys = videoHotkeys;

  // 7.7. Khi bị khóa lại lúc đang dùng (đăng xuất / hết phiên): dừng ngay âm thanh, video, phòng Caro.
  authGate.onLock = () => {
    const stopSafely = (fn) => { try { fn(); } catch (e) {} };
    stopSafely(() => videoPlayerEngine.emergencyMuteAndPause());
    stopSafely(() => ttsEngine.stop());
    stopSafely(() => ttsControlBar.hide());
    stopSafely(() => emulatorController.deactivate());
    stopSafely(() => figmaController.deactivate());
    stopSafely(() => vscodeController.deactivate());
    stopSafely(() => calendarController.leaveView());
  };

  // 8. KÍCH HOẠT HASH ROUTER ĐIỀU PHỐI MÀN HÌNH TỰ ĐỘNG — chỉ sau khi đã đăng nhập
  authGate.whenAuthenticated(() => {
    console.log('[Khởi tạo] Kích hoạt hệ thống Hash Router trung tâm...');
    appRouter.route();

    // Nếu trên URL có param ?q=... => Tự động mở modal tìm kiếm từ khóa đó
    const urlParams = UrlService.getParams();
    if (urlParams.q) {
      console.log('[Deep Link] Tìm kiếm từ URL:', urlParams.q);
      searchModal.triggerSearch(urlParams.q, urlParams.source || DEFAULT_SOURCE, false);
    }

    console.log('Hệ thống ngụy trang đọc truyện & Video Editor đa nền tảng đã sẵn sàng!');
  });
}

// Khởi chạy ứng dụng an toàn theo trạng thái nạp trang
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  // DOM đã sẵn sàng (trường hợp script nạp sau hoặc browser cache)
  initApp();
}
